import {
  Play,
  Building2,
  Code,
  CheckCircle,
  XCircle,
  Terminal,
  Maximize2,
  FileText,
  Minimize2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import Button from '../components/Common/Button';
import CommonDialog from '../components/Common/Dialog';
import Dropdown from '../components/Common/Dropdown';
import TestFooter from '../components/Features/PracticeTest/TestFooter';
import TestHeader from '../components/Features/PracticeTest/TestHeader';
import UpgradePlanPopUP from '../components/ReUsableComponents/UpgradePlanPopUP';
import {
  setPlansPopUpOpen,
  setpopUpPlanMessage
} from '../redux/Slices/PaymentsSlice';
import { setSelectedOption } from '../redux/Slices/ProgrammingSlice';
import {
  resetCompleteState,
  resetState,
  setIsStartTest
} from '../redux/Slices/QuestionsSlice';
import { setCurrentTab } from '../redux/Slices/ResultsSlice/ResultsSlice';
import {
  resetTestSettings,
  setCurrentCodeingQuestionAnswer,
  setCurrentQuestionIndex,
  setCurrentSectionIndex,
  setCurrentTime,
  setIsResultsScreen,
  setResetSatrtFullTest,
  setShowInstructions
} from '../redux/Slices/StartTestSlice/startFulTest';
import { useAppDispatch, useAppSelector } from '../redux/store/hooks';
import {
  useCompileProgramMutation,
  useGetCodeingEvaluationMutation,
  useGetProgrammingTopicsQuery,
  useLazyStartProgrammingTestQuery,
  useSubmitProgrammingTestMutation
} from '../services/startTestAPI/startTestApi';

import EditorPage from './EditorPage';

export type TopicOption = {
  id: string;
  topicName: string;
};

// Answer structure (when user submits)
export type Answer = {
  questionId: string;
  userSelectedOption: string;
  isCorrectAnswer: boolean;
  actualCorrectOption: string;
  userSubmittedCode: string;
  languageUsed: string;
  aiGeneratedAnswer: string;
  explanation: string;
};

// Question object
export type Question = {
  id: string;
  isActive: boolean;
  createdDate: string;
  questionTypeId: string;
  roundId: string;
  sectionId: string;
  topicId: string;
  subTopic: string;
  questionText: string;
  explanation: string | null;
  isProgrammingQuestion: boolean;
  options: string[] | null;
  sourceNameId: string;
  correctAnswer: string | null;
  questionStatus: number;
  idealSolutions?: {
    answerCode: string;
    explanation: string;
    language: string;
  }[];
  starterCode?: {
    language: string;
    code: string;
  }[];
  answers?: Answer[] | null; // optional, can be null until answered
};

// Whole QuestionData wrapper
export type QuestionData = {
  id: string;
  isActive: boolean;
  userId: string;
  createdDate: string;
  updatedDate: string;
  question: Question;
  answers?: Answer | null;
  testStatus: string;
};

type DifficultyOption = {
  value: number;
  label: string;
};

export const CompilerOutput = ({
  result
}: {
  result: {
    success: boolean;
    output: string;
    error: string;
  } | null;
}) => {
  if (!result) return null;

  const isSuccess = result.success;

  return (
    <div className='h-[21vh] rounded-lg border'>
      {/* Header */}
      <div
        className={`flex items-center gap-2 px-4 py-3 ${
          isSuccess
            ? 'border-b border-green-200 bg-green-50'
            : 'border-b border-red-200 bg-red-50'
        }`}
      >
        <Terminal
          className={`h-5 w-5 ${isSuccess ? 'text-green-600' : 'text-red-600'}`}
        />
        <span
          className={`font-semibold ${isSuccess ? 'text-green-800' : 'text-red-800'}`}
        >
          {isSuccess ? 'Compilation Successful' : 'Compilation Failed'}
        </span>
        {isSuccess ? (
          <CheckCircle className='ml-auto h-5 w-5 text-green-600' />
        ) : (
          <XCircle className='ml-auto h-5 w-5 text-red-600' />
        )}
      </div>

      {/* Output Content */}
      <div
        className='overflow-auto rounded-b-lg bg-gray-900 p-4 font-mono text-sm text-gray-100'
        style={{ height: 'calc(100% - 49px)' }}
      >
        {isSuccess ? (
          <div>
            <div className='mb-2 text-green-400'>Output:</div>
            <pre className='whitespace-pre-wrap'>{result.output}</pre>
          </div>
        ) : (
          <div>
            <div className='mb-2 text-red-400'>Error:</div>
            <pre className='whitespace-pre-wrap text-red-300'>
              {result.error}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

const ProgrammingTest = () => {
  const [selectedDifficulty, setSelectedDifficulty] =
    useState<DifficultyOption | null>(null);
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false); // New state for submission loading
  const dispatch = useAppDispatch();
  const { written, isStartTest } = useAppSelector(
    state => state.root.questions
  );
  const { selectedOption } = useAppSelector(state => state.root.programming);
  const [questionData, setQuestionData] = useState<QuestionData>(
    {} as QuestionData
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { currentTime } = useAppSelector(state => state.root.startFulltest);
  const {
    roundName,
    currentQuestionIndex,
    currentSectionName,
    allCodeingQuestions,
    selectedCodeingLanguage,
    currentCodeingQuestionAnswer
  } = useAppSelector(state => state.root.startFulltest);
  const [compileResult, setCompileResult] = useState(null);

  const { currentData } = useGetProgrammingTopicsQuery({});
  const [startProgrammingTest, { isLoading: isStartTestLoading }] =
    useLazyStartProgrammingTestQuery();
  const [submitProgrammingTest] = useSubmitProgrammingTestMutation();
  const [evaluateCode] = useGetCodeingEvaluationMutation();
  const [compileProgram] = useCompileProgramMutation();
  const navigate = useNavigate();
  const [isCompiling, setIsCompiling] = useState(false);

  const [maximizedPanel, setMaximizedPanel] = useState<
    'description' | 'code' | null
  >(null);

  useEffect(() => {
    if (currentTime <= 0 && isStartTest) {
      handleSubmit();
    }
  }, [currentTime, isStartTest]);

  const handleNext = () => {
    if (currentQuestionIndex < written.length - 1) {
      dispatch(setCurrentCodeingQuestionAnswer(''));
      dispatch(setCurrentQuestionIndex(currentQuestionIndex + 1));
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      dispatch(setCurrentQuestionIndex(currentQuestionIndex - 1));
    }
  };

  const hadleQuestionJump = (number: number) => {
    dispatch(setCurrentQuestionIndex(number));
  };

  useEffect(() => {
    dispatch(setCurrentTime(1800));
  }, [currentQuestionIndex]);

  const difficultyOptions: DifficultyOption[] = [
    { value: 1, label: 'Easy' },
    { value: 2, label: 'Medium' },
    { value: 3, label: 'Hard' }
  ];

  const evaluateCodeingQuestions = async () => {
    try {
      let finalAnswer: any = null;

      for (const each of allCodeingQuestions) {
        if (each?.questionId !== null || each?.questionId !== undefined) {
          try {
            const evaluatedCodeResponse = await evaluateCode(each);

            finalAnswer = {
              questionId: each?.questionId,
              userSelectedOption: '',
              isCorrectAnswer:
                evaluatedCodeResponse.data?.data?.isProvidedAnswerCorrect,
              actualCorrectOption: '',
              userSubmittedCode: each.userProvidedSoluation,
              languageUsed: each.language,
              aiGeneratedAnswer: evaluatedCodeResponse.data?.data?.idealAnswer,
              explanation: evaluatedCodeResponse.data?.data?.whatWentWrong,
              testCaseResults:
                evaluatedCodeResponse.data?.data?.testCaseResults,
              testCaseSummary:
                evaluatedCodeResponse.data?.data?.testCaseSummary,
              areasOfImprovement:
                evaluatedCodeResponse.data?.data?.areasOfImprovement,
              isPlagiarismCheckPassed:
                evaluatedCodeResponse.data?.data?.isPlagiarismCheckPassed
            };

            // overwrite, so only latest one stays
          } catch (error) {
            console.error('Error evaluating code:', error);
            toast.error('Error evaluating code submission');
            throw error; // Re-throw to be caught by handleSubmit
          }
        }
      }

      // return only one object
      return { answers: finalAnswer };
    } catch (error) {
      console.error('Error in evaluateCodeingQuestions:', error);
      throw error; // Re-throw to be caught by handleSubmit
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true); // Start submission loading
    try {
      dispatch(setCurrentQuestionIndex(0));
      dispatch(resetState());
      const finalAnswers = await evaluateCodeingQuestions();

      const saveResponse = await submitProgrammingTest({
        ...questionData,
        testStatus: 'COMPLETED',
        answers: [finalAnswers.answers],
        question: {
          ...questionData.question
        }
      });

      if (saveResponse.data.status === 'success') {
        toast.success('Test submitted successfully');
        dispatch(setIsStartTest(false));
        dispatch(setCurrentSectionIndex(0));
        dispatch(resetCompleteState());
        dispatch(setResetSatrtFullTest());
        dispatch(resetTestSettings());
        setIsSubmitting(false); // Stop submission loading
        dispatch(setIsResultsScreen(true)); // Show results screen
        navigate('/results');
        dispatch(setCurrentTab('programming'));
        setQuestionData({
          ...questionData,
          testStatus: 'COMPLETED',
          question: {
            ...questionData.question,
            answers: [finalAnswers.answers]
          }
        });
      } else {
        setIsSubmitting(false); // Stop submission loading
        toast.error(saveResponse.data.message || 'Error saving test');
      }
    } catch (error) {
      setIsSubmitting(false); // Stop submission loading
      console.error('Error in handleSubmit:', error);
      toast.error('Error submitting test. Please try again.');
    }
  };

  const handleViewResults = () => {
    dispatch(setIsResultsScreen(true)); // Show results screen
  };

  const handleStartTest = async () => {
    try {
      dispatch(setShowInstructions(false));
      const res = await startProgrammingTest({
        topicId: selectedOption?.id ?? ''
      }).unwrap();
      setQuestionData(res?.data);
      dispatch(setIsStartTest(true));
    } catch (error: any) {
      console.error('Error in handleStartTest:', error);
      if (error?.status === 402) {
        const message = error?.data?.message;
        dispatch(setpopUpPlanMessage(message));
        dispatch(setPlansPopUpOpen(true));
      }
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const message = 'Changes you made may not be saved.';
      e.returnValue = message;
      return message;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const handleBackButton = (event: any) => {
      const confirmBack = window.confirm(
        'Are you sure you want to leave this page? Any changes you have made will be lost, and youll need to start the test over from the beginning.'
      );
      if (!confirmBack) {
        dispatch(setIsStartTest(false));
        event.preventDefault();
      }
    };

    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', handleBackButton);

    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [location, navigate]);

  useEffect(() => {
    if (!currentCodeingQuestionAnswer || currentCodeingQuestionAnswer === '') {
      setCompileResult(null);
    }
  }, [currentCodeingQuestionAnswer]);

  const onStartNewTest = () => {
    dispatch(setIsStartTest(false));
    dispatch(setCurrentSectionIndex(0));
    dispatch(resetCompleteState());
    dispatch(setResetSatrtFullTest());
    dispatch(setCurrentTime(1800));
    dispatch(resetTestSettings());
    dispatch(setIsResultsScreen(false)); // Show results screen
    setQuestionData({} as QuestionData);
    setSelectedDifficulty(null);
    dispatch(setSelectedOption(null));
  };

  const displayRun =
    selectedCodeingLanguage === 'java' && currentCodeingQuestionAnswer;

  // Show loading screen with disabled View Results button during submission
  if (isSubmitting) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100'>
        <div className='mx-auto max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl'>
          <div className='mx-auto mb-6 h-16 w-16 animate-spin rounded-full border-b-2 border-indigo-600'></div>
          <h2 className='mb-4 text-2xl font-bold text-gray-800'>
            Submitting Test...
          </h2>
          <p className='mb-6 text-gray-600'>
            Please wait while we evaluate your code and save your results.
          </p>
          <button
            disabled={true}
            className='w-full cursor-not-allowed rounded-2xl bg-gray-200 px-6 py-4 text-lg font-semibold text-gray-500'
          >
            View Results
          </button>
        </div>
      </div>
    );
  }

  // Show loading screen for other loading states
  if (isLoading) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100'>
        <div className='mx-auto max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl'>
          <div className='mx-auto mb-6 h-16 w-16 animate-spin rounded-full border-b-2 border-indigo-600'></div>
          <h2 className='mb-4 text-2xl font-bold text-gray-800'>Loading...</h2>
          <p className='mb-6 text-gray-600'>
            Please wait while we process your request.
          </p>
          <button
            disabled={isLoading}
            onClick={handleViewResults}
            className={`w-full rounded-2xl px-6 py-4 text-lg font-semibold ${
              isLoading
                ? 'cursor-not-allowed bg-gray-200 text-gray-500'
                : 'bg-primary text-white hover:bg-primary/90'
            }`}
          >
            View Results
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {isStartTest && questionData?.id ? (
        <div className='flex h-full flex-col overflow-hidden overscroll-contain bg-white'>
          <TestHeader
            title={'Coding Assessment'}
            time='0:00'
            currentSectionName={selectedOption?.topicName || ''}
            shouldShowTimer={false}
            closeFunction={() => {
              setIsExitDialogOpen(true);
            }}
          />
          <div className='text-s mx-[45px] flex h-[35px] items-center justify-center rounded-b-2xl bg-primary py-2 text-center font-semibold text-white'>
            {selectedOption?.topicName}
          </div>
          <div className='flex w-full items-center justify-center gap-[2px] px-6 py-3'>
            <button
              className={`rounded-l-lg px-3 py-2 text-white ${isCompiling || !displayRun ? 'bg-gray-400' : 'bg-primary'}`}
              disabled={isCompiling || !displayRun}
              onClick={async () => {
                setIsCompiling(true);
                try {
                  const res = await compileProgram({
                    language: selectedCodeingLanguage?.toUpperCase(),
                    code: currentCodeingQuestionAnswer
                  }).unwrap();
                  setCompileResult(res);
                  setIsCompiling(false);
                } catch (error) {
                  setIsCompiling(false);
                  setCompileResult((error as any)?.data);
                }
              }}
            >
              <Play strokeWidth={1.5} />
            </button>
            <button
              className='rounded-r-lg bg-primary px-3 py-2 font-semibold text-white'
              disabled={isSubmitting}
              onClick={handleSubmit}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
          <div
            // style={{ height: 'calc(100vh - 290px)' }}
            className='flex w-full overflow-auto lg:px-10'
          >
            <div className='flex h-full w-full justify-center'>
              <div className='relative flex w-full flex-col gap-4 p-2 md:px-4 lg:flex-row'>
                <div
                  className={`rounded-lg border border-primary transition-all duration-300 lg:h-[67vh] lg:overflow-y-auto
                  ${maximizedPanel === 'code' ? 'lg:w-[4%]' : ''}
                  ${maximizedPanel === 'description' ? 'lg:w-[96%]' : ''}
                  ${!maximizedPanel ? 'lg:w-1/2' : ''}
                  w-full`}
                >
                  <div
                    className={`flex items-center rounded-t-lg bg-sky-200 p-2 text-sm ${
                      maximizedPanel === 'code'
                        ? 'h-full flex-col justify-start'
                        : 'flex-row justify-between'
                    }`}
                  >
                    <div
                      className={`flex items-center gap-2 ${maximizedPanel === 'code' ? 'flex-col' : 'flex-row'}`}
                    >
                      {!(maximizedPanel === 'code') && (
                        <FileText className='h-4 w-4 text-black' />
                      )}
                      {maximizedPanel === 'code' && (
                        <ChevronRight
                          className='mt-2 h-6 w-6 cursor-pointer text-black'
                          onClick={() => setMaximizedPanel(null)}
                        />
                      )}
                      <span
                        className={`text-lg font-normal text-black ${
                          maximizedPanel === 'code'
                            ? 'rotate-180 [writing-mode:vertical-rl]'
                            : ''
                        }`}
                      >
                        Description
                      </span>
                    </div>
                    <div
                      className={`hidden lg:block ${maximizedPanel === 'code' ? 'hidden' : ''}`}
                    >
                      {maximizedPanel === 'description' && (
                        <Minimize2
                          className='h-4 w-4 cursor-pointer'
                          onClick={() => setMaximizedPanel(null)}
                        />
                      )}
                      {maximizedPanel === null && (
                        <Maximize2
                          className='h-4 w-4 cursor-pointer'
                          onClick={() => setMaximizedPanel('description')}
                        />
                      )}
                    </div>
                  </div>
                  <div
                    className={`bg-white px-2 py-6 font-medium lg:px-6 ${maximizedPanel === 'code' ? 'hidden' : 'block'}`}
                    dangerouslySetInnerHTML={{
                      __html: questionData?.question?.questionText ?? ''
                    }}
                  />
                </div>

                <div
                  className={`rounded-lg border border-primary transition-all duration-300 lg:overflow-y-auto
                  ${maximizedPanel === 'description' ? 'lg:w-[4%]' : ''}
                  ${maximizedPanel === 'code' ? 'lg:w-[96%]' : ''}
                  ${!maximizedPanel ? 'lg:w-1/2' : ''}
                  ${compileResult && (!maximizedPanel || maximizedPanel === 'code') ? 'lg:h-[45vh]' : 'lg:h-[67vh]'}
                  mb-6 w-full lg:mb-0`}
                >
                  <div
                    className={`flex items-center rounded-t-lg bg-sky-200 p-2 text-sm ${
                      maximizedPanel === 'description'
                        ? 'h-full flex-col justify-start'
                        : 'flex-row justify-between'
                    }`}
                  >
                    <div
                      className={`flex items-center gap-2 ${maximizedPanel === 'description' ? 'flex-col' : 'flex-row'}`}
                    >
                      {!(maximizedPanel === 'description') && (
                        <Code className='h-4 w-4 text-black' />
                      )}

                      {maximizedPanel === 'description' && (
                        <ChevronLeft
                          className='mt-2 h-6 w-6 cursor-pointer text-black'
                          onClick={() => setMaximizedPanel(null)}
                        />
                      )}

                      <span
                        className={`text-lg font-normal text-black ${
                          maximizedPanel === 'description'
                            ? 'rotate-180 [writing-mode:vertical-rl]'
                            : ''
                        }`}
                      >
                        Code
                      </span>
                    </div>
                    <div
                      className={`hidden lg:block ${maximizedPanel === 'description' ? 'hidden' : ''}`}
                    >
                      {maximizedPanel === 'code' && (
                        <Minimize2
                          className='h-4 w-4 cursor-pointer'
                          onClick={() => setMaximizedPanel(null)}
                        />
                      )}
                      {maximizedPanel === null && (
                        <Maximize2
                          className='h-4 w-4 cursor-pointer'
                          onClick={() => setMaximizedPanel('code')}
                        />
                      )}
                    </div>
                  </div>
                  <div
                    className={`mx-auto w-full ${maximizedPanel === 'description' ? 'hidden' : 'block'}`}
                  >
                    <EditorPage
                      questionNumber={1}
                      questionText={questionData?.question?.questionText}
                      questionId={questionData?.question?.id}
                      sectionId={questionData?.question?.sectionId}
                      compileResult={compileResult}
                      starterCode={questionData?.question?.starterCode ?? []}
                      shouldShowStarterCode={true}
                    />
                  </div>
                </div>
                <div
                  className={`absolute bottom-0 right-1 px-3 py-2  ${maximizedPanel === 'code' ? 'w-[calc(100%-4%-22px)]' : 'w-1/2'} ${maximizedPanel === 'description' ? 'hidden' : 'block'}`}
                >
                  <CompilerOutput result={compileResult} />
                </div>
              </div>
            </div>
          </div>

          <TestFooter
            // onBack={handleBack}
            // onNext={handleNext}
            // onSubmit={handleSubmit}
            questionNumber={0}
            // disableBack={true}
            // disableNext={true}
            isProgrammingQuestion={true}
            hadleQuestionJump={hadleQuestionJump}
            isProgrammingTest={true}
            isLoading={isSubmitting}
            examName='Programming Assesment'
            shouldShowTimer={false}
          />
          <CommonDialog
            close={() => setIsExitDialogOpen(false)}
            isOpen={isExitDialogOpen}
          >
            <div className='flex flex-col items-center justify-center p-4'>
              <h2 className='mb-4 text-lg font-semibold'>
                Are you sure you want to leave the test?
              </h2>
              <p className='mb-4 text-sm '>Your Progress will not be saved.</p>
              <Button
                label={'Exit Test'}
                disabled={isLoading}
                handleClickBtn={() => {
                  dispatch(setResetSatrtFullTest());
                  dispatch(setSelectedOption(null));
                  dispatch(setIsStartTest(false));
                  setIsExitDialogOpen(false);
                }}
                variant='contained'
              />
            </div>
          </CommonDialog>
        </div>
      ) : (
        <div className=''>
          <div className='container mx-auto px-4 pb-12'>
            <div className='mx-auto max-w-2xl'>
              {/* Header Section */}
              <div className='my-5 text-center'>
                <p className='mx-auto max-w-md text-lg text-gray-600'>
                  Test your coding skills with topic-specific programming
                  challenges
                </p>
              </div>

              {/* Main Card */}
              <div className='rounded-3xl bg-white p-8 shadow-2xl md:p-10'>
                {/* Topic and Difficulty Selection Section */}
                <div className='mb-8'>
                  <div className='mb-4 flex items-center'>
                    <Building2 className='mr-2 h-5 w-5 text-indigo-600' />
                    <label className='text-lg font-semibold text-gray-800'>
                      Test Configuration
                    </label>
                    <span className='ml-1 text-red-500'>*</span>
                  </div>
                  <p className='mb-6 text-sm text-gray-600'>
                    Choose a topic to practice interview-style questions
                  </p>

                  <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                    {/* Topic Dropdown */}
                    <div className='relative md:col-span-2'>
                      <label className='mb-2 block text-sm font-medium text-gray-700'>
                        Topic <span className='text-red-500'>*</span>
                      </label>
                      <Dropdown
                        disable={
                          currentData?.data?.length === 0 || !currentData
                        }
                        getOption={(option: TopicOption) => option.topicName}
                        options={
                          currentData?.data ?? [
                            { topicName: 'No Topics Available' }
                          ]
                        }
                        label=''
                        value={selectedOption?.topicName || ''}
                        handleDropdownChange={(option: TopicOption) =>
                          dispatch(setSelectedOption(option))
                        }
                        width='w-full max-w-full'
                        placeholder='Choose a topic...'
                        classes='cursor-pointer'
                      />
                    </div>

                    {/* Difficulty Dropdown */}
                    {/* <div className='relative'>
                      <label className='mb-2 block text-sm font-medium text-gray-700'>
                        Difficulty Level
                      </label>
                      <Dropdown
                        getOption={(option: DifficultyOption) => option.label}
                        options={difficultyOptions}
                        label=''
                        value={selectedDifficulty?.label || ''}
                        handleDropdownChange={(option: DifficultyOption) =>
                          setSelectedDifficulty(option)
                        }
                        width='w-full'
                        placeholder='Select difficulty...'
                        classes='cursor-pointer'
                      />
                    </div> */}
                  </div>

                  {/* Selected Options Preview */}
                  {(selectedOption || selectedDifficulty) && (
                    <div className='mt-6 grid grid-cols-1 gap-3 md:grid-cols-2'>
                      {selectedOption && (
                        <div className='rounded-xl border border-green-200 bg-green-50 p-4'>
                          <div className='flex items-center'>
                            <CheckCircle className='mr-2 h-4 w-4 text-green-600' />
                            <span className='text-sm font-medium text-green-800'>
                              Topic: {selectedOption?.topicName}
                            </span>
                          </div>
                        </div>
                      )}
                      {selectedDifficulty && (
                        <div className='rounded-xl border border-blue-200 bg-blue-50 p-4'>
                          <div className='flex items-center'>
                            <CheckCircle className='mr-2 h-4 w-4 text-blue-600' />
                            <span className='text-sm font-medium text-blue-800'>
                              Level: {selectedDifficulty?.label}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className='mb-8 rounded-xl bg-gray-50 p-6'>
                  <h3 className='mb-3 font-semibold text-gray-800'>
                    Instructions:
                  </h3>
                  <ul className='space-y-2 text-sm text-gray-600'>
                    <li className='flex items-start'>
                      <span className='mr-3 mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-indigo-400'></span>
                      Read each question carefully before starting to code
                    </li>
                    <li className='flex items-start'>
                      <span className='mr-3 mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-indigo-400'></span>
                      Test your solution with the provided examples
                    </li>
                    <li className='flex items-start'>
                      <span className='mr-3 mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-indigo-400'></span>
                      Submit when you are confident in your answer
                    </li>
                  </ul>
                </div>

                {/* Start Button */}
                <button
                  onClick={handleStartTest}
                  disabled={!selectedOption || isStartTestLoading}
                  className={`
                flex w-full transform items-center justify-center space-x-3
                rounded-2xl px-6 py-4 text-lg
                font-semibold transition-all duration-300
                ${
                  !selectedOption || isStartTestLoading
                    ? 'cursor-not-allowed bg-gray-200 text-gray-500'
                    : 'bg-primary text-white shadow-lg hover:scale-[1.02] hover:from-indigo-700 hover:to-purple-700 hover:shadow-xl active:scale-[0.98]'
                }
              `}
                >
                  <Play className='h-6 w-6' />
                  <span>Start Coding Assessment</span>
                </button>

                {/* Validation Message */}
                {!selectedOption && (
                  <p className='mt-4 text-center text-sm text-gray-500'>
                    Please select a topic to begin the assessment
                  </p>
                )}
              </div>
            </div>
          </div>
          <UpgradePlanPopUP />
        </div>
      )}
    </>
  );
};

export default ProgrammingTest;
